import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission, hasPermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * `/masters/attendance-reason-codes` (v0.4後半 月次実績一括入力)。
 * `attendance_reason_codes` (`apps/api/src/routes/shift-reference-masters.ts`) の管理画面。
 * ⚠️ このテーブル自体は v0.4 章④ Part 3 (D-112) から存在し late/early_leave/shift_change/
 * staffing_override の4カテゴリを既に使っていたが、CRUD APIはあっても管理画面が一度も無かった
 * (実測で判明)。今回 holiday_work カテゴリ・linked_leave_type_id 列を追加するのに合わせ、
 * 既存4カテゴリも含めた汎用マスタ管理画面として実装する。
 * 設計: docs/design/v04-monthly-attendance-bulk-entry-detail.md §3-3
 *
 * テナント全体で共有される定義のため own_store の manage 保有者には書換えを許さない
 * (バックエンドの requireTenantWideManage と同じ規約、§8-50)。
 */

const CATEGORIES = ['late', 'early_leave', 'absence', 'shift_change', 'staffing_override', 'holiday_work'] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_LABELS: Record<Category, string> = {
  late: '遅刻', early_leave: '早退', absence: '欠勤・休暇',
  shift_change: 'シフト変更', staffing_override: '人員調整', holiday_work: '休日出勤',
};

// v0.4後半 月次実績一括入力 第2フェーズ I-0 (§2-2): 区分の意味。category='absence' のコードにのみ設定できる。
const ABSENCE_KINDS = ['scheduled_off', 'paid_leave', 'compensatory_off', 'absence', 'other_leave'] as const;
type AbsenceKind = (typeof ABSENCE_KINDS)[number];
const ABSENCE_KIND_LABELS: Record<AbsenceKind, string> = {
  scheduled_off: '公休', paid_leave: '有給休暇', compensatory_off: '代休', absence: '欠勤', other_leave: 'その他休暇',
};

type ReasonCode = {
  reason_code_id: string; category: Category; code: string; label: string;
  sort_order: number; linked_leave_type_id: string | null;
  absence_kind: AbsenceKind | null; employment_type_ids: string[];
};
type LeaveType = { leave_type_id: string; label: string };
type EmploymentType = { type_id: string; code: string; label: string; effective_to: string | null };

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const NO_LINK_VALUE = '__none__';
const NO_ABSENCE_KIND_VALUE = '__none__';

type Draft = {
  reason_code_id: string | null;
  category: Category;
  code: string;
  label: string;
  sort_order: string;
  linked_leave_type_id: string;
  absence_kind: string;
  employment_type_ids: string[];
};

export function AttendanceReasonCodesPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'shift_attendance_reason_master', 'manage');
  const canViewLeaveTypes = hasPermission(user?.effective_permissions, 'leave_type', 'view');
  const qc = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<Category | ''>('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReasonCode | null>(null);

  const { data, isLoading } = useQuery<{ reason_codes: ReasonCode[] }>({
    queryKey: ['attendance-reason-codes-all'],
    queryFn: () => apiFetch<{ reason_codes: ReasonCode[] }>('/masters/attendance-reason-codes'),
  });
  const codes = data?.reason_codes ?? [];
  const filtered = categoryFilter ? codes.filter((c) => c.category === categoryFilter) : codes;
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (a.category === b.category ? a.sort_order - b.sort_order : a.category.localeCompare(b.category))),
    [filtered],
  );

  // ⚠️ 「休暇管理と連動」を選ぶドロップダウン用。leave_type:view 権限が無い利用者
  // (shift:manage all_stores のみ保有等) では取得できないため、その場合は連動選択肢自体を隠す
  // (画面全体をエラーにしない、§6-0原則3)。
  const { data: leaveTypesData } = useQuery<LeaveType[]>({
    queryKey: ['leave-types-for-reason-codes'],
    queryFn: () => apiFetch<LeaveType[]>('/leave-types'),
    enabled: canViewLeaveTypes,
  });
  const leaveTypes = leaveTypesData ?? [];
  const leaveTypeById = useMemo(() => new Map(leaveTypes.map((lt) => [lt.leave_type_id, lt.label])), [leaveTypes]);

  // v0.4後半 月次実績一括入力 第2フェーズ I-0 (§2-3): 雇用区分ごとの表示制限用。GET /employment-types は
  // 認証済みテナントユーザーなら誰でも取得できる (employee-masters.ts、権限チェック無し)。
  const { data: employmentTypesData } = useQuery<EmploymentType[]>({
    queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types'),
  });
  const employmentTypes = useMemo(() => (employmentTypesData ?? []).filter((t) => !t.effective_to), [employmentTypesData]);
  const employmentTypeById = useMemo(() => new Map(employmentTypes.map((t) => [t.type_id, t.label])), [employmentTypes]);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const payload = {
        category: d.category, code: d.code.trim(), label: d.label.trim(), sort_order: Number(d.sort_order) || 0,
        linked_leave_type_id: d.category === 'absence' && d.linked_leave_type_id !== NO_LINK_VALUE ? d.linked_leave_type_id : null,
        absence_kind: d.category === 'absence' && d.absence_kind !== NO_ABSENCE_KIND_VALUE ? d.absence_kind : null,
        employment_type_ids: d.employment_type_ids,
      };
      return d.reason_code_id
        ? apiFetch(`/masters/attendance-reason-codes/${d.reason_code_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            label: payload.label, sort_order: payload.sort_order, linked_leave_type_id: payload.linked_leave_type_id,
            absence_kind: payload.absence_kind, employment_type_ids: payload.employment_type_ids,
          }),
        })
        : apiFetch('/masters/attendance-reason-codes', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance-reason-codes-all'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/attendance-reason-codes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance-reason-codes-all'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  // user要望 (2026-09-26): 並び順を変更するUIがこれまで無かった (sort_orderはPUTで既に更新可能
  // だったが、編集モーダルに入力欄が無く実質変更手段が無かった)。同カテゴリ内の隣接する2件の
  // sort_orderを入れ替える方式にする。
  const swapMut = useMutation({
    mutationFn: async ({ a, b }: { a: ReasonCode; b: ReasonCode }) => {
      await apiFetch(`/masters/attendance-reason-codes/${a.reason_code_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: b.sort_order }) });
      await apiFetch(`/masters/attendance-reason-codes/${b.reason_code_id}`, { method: 'PUT', body: JSON.stringify({ sort_order: a.sort_order }) });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-reason-codes-all'] }),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '並び順の変更に失敗しました'),
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  // v0.4後半 月次実績一括入力 第2フェーズ I-0 (§2-2): absence_kind未設定の「欠勤・休暇」区分が
  // あると、変形労働時間制の集計が determinable:false になりうる (実際の判定は集計エンジン側、
  // ここでは設定漏れへの気づきを促す警告のみ表示する)。
  const missingAbsenceKindCodes = codes.filter((c) => c.category === 'absence' && !c.absence_kind);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">勤怠区分マスタ</h1>
        <p className="text-sm text-text-sub mt-1">
          遅刻・早退・欠勤・休暇・シフト変更・人員調整・休日出勤等の理由コードを管理します。
          月次実績一括入力の「区分」選択肢もここから登録します。
        </p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です（登録・編集には全店舗の管理権限が必要です）。</p>}
      </div>

      {missingAbsenceKindCodes.length > 0 && (
        <div className="rounded-lg border border-alert-border bg-alert-bg px-3 py-2 text-sm text-alert-text">
          「区分の意味」が未設定の欠勤・休暇区分が{missingAbsenceKindCodes.length}件あります
          ({missingAbsenceKindCodes.map((c) => c.label).join('、')})。
          変形労働時間制を使う雇用区分でこれらの区分が使われると、集計が正しく行えない場合があります。
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0 w-full sm:w-56">
          <label className="block text-xs text-text-sub mb-1">カテゴリで絞り込み</label>
          <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v as Category)}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="全カテゴリ" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">全カテゴリ</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({
              reason_code_id: null, category: categoryFilter || 'absence', code: '', label: '',
              sort_order: String(codes.length), linked_leave_type_id: NO_LINK_VALUE,
              absence_kind: NO_ABSENCE_KIND_VALUE, employment_type_ids: [],
            })}
          >区分を追加</Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : sorted.length === 0 ? (
        <EmptyState message="区分がまだありません" description={canManage ? '「区分を追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {sorted.map((c, i) => {
            // ⚠️ sorted は「カテゴリ→sort_order」の順に並んでいるため、同カテゴリの項目は
            // 配列内で必ず連続する。前後の要素のcategoryを見るだけで「同カテゴリ内の先頭/末尾か」を判定できる。
            const prev = sorted[i - 1];
            const next = sorted[i + 1];
            const canMoveUp = !!prev && prev.category === c.category;
            const canMoveDown = !!next && next.category === c.category;
            return (
              <li key={c.reason_code_id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white p-3 flex-wrap">
                <div className="min-w-0 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{CATEGORY_LABELS[c.category]}</span>
                  <span className="text-sm font-semibold text-text-body break-words">{c.label}</span>
                  <span className="text-[11px] text-text-sub whitespace-nowrap">({c.code})</span>
                  {c.linked_leave_type_id && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 whitespace-nowrap">
                      休暇管理と連動: {leaveTypeById.get(c.linked_leave_type_id) ?? c.linked_leave_type_id}
                    </span>
                  )}
                  {c.category === 'absence' && (
                    <span className={`text-[11px] px-1.5 py-0.5 rounded whitespace-nowrap ${c.absence_kind ? 'bg-surface-sunken text-text-sub' : 'bg-alert-bg text-alert-text'}`}>
                      {c.absence_kind ? ABSENCE_KIND_LABELS[c.absence_kind] : '区分の意味 未設定'}
                    </span>
                  )}
                  {c.employment_type_ids.length > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 whitespace-nowrap">
                      対象雇用区分: {c.employment_type_ids.map((id) => employmentTypeById.get(id) ?? id).join('・')}
                    </span>
                  )}
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" aria-label="上に移動" disabled={!canMoveUp || swapMut.isPending}
                      onClick={() => prev && swapMut.mutate({ a: c, b: prev })}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring disabled:opacity-30 disabled:pointer-events-none"><ChevronUp size={14} /></button>
                    <button type="button" aria-label="下に移動" disabled={!canMoveDown || swapMut.isPending}
                      onClick={() => next && swapMut.mutate({ a: c, b: next })}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring disabled:opacity-30 disabled:pointer-events-none"><ChevronDown size={14} /></button>
                    <button type="button" aria-label="編集"
                      onClick={() => setDraft({
                        reason_code_id: c.reason_code_id, category: c.category, code: c.code, label: c.label,
                        sort_order: String(c.sort_order), linked_leave_type_id: c.linked_leave_type_id ?? NO_LINK_VALUE,
                        absence_kind: c.absence_kind ?? NO_ABSENCE_KIND_VALUE, employment_type_ids: c.employment_type_ids,
                      })}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                    <button type="button" aria-label="削除" onClick={() => setDeleteTarget(c)}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.reason_code_id ? '区分を編集' : '区分を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">カテゴリ</label>
              <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v as Category })} disabled={!!draft.reason_code_id}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50}
                className={inputCls} placeholder="例: paid_leave" disabled={!!draft.reason_code_id} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200}
                className={inputCls} placeholder="例: 有給休暇" />
            </div>
            {draft.category === 'absence' && (canViewLeaveTypes ? (
              <div>
                <label className="block text-sm text-text-sub mb-1">休暇管理との連動 (任意)</label>
                <Select value={draft.linked_leave_type_id} onValueChange={(v) => setDraft({ ...draft, linked_leave_type_id: v })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_LINK_VALUE}>連動させない（単純なタグとして使う）</SelectItem>
                    {leaveTypes.map((lt) => <SelectItem key={lt.leave_type_id} value={lt.leave_type_id}>{lt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-text-sub mt-1">
                  連動させると、この区分を選んだ日は休暇管理の残数チェック・消化が行われます。
                  未設定のままなら、休暇管理を使っていなくても残数に関係なく選択できます。
                </p>
              </div>
            ) : (
              // ⚠️ design-expert MEDIUM反映: leave_type:view 権限が無いと編集モーダルから
              // 連動セレクトごと消え、既存の連動設定が読み取り専用でも確認できなくなっていた
              // (一覧側は表示される点と非対称)。編集権限は無いままでも状態だけは見せる。
              <div>
                <label className="block text-sm text-text-sub mb-1">休暇管理との連動</label>
                <p className="text-sm text-text-body">
                  {draft.linked_leave_type_id !== NO_LINK_VALUE ? `連動あり (${draft.linked_leave_type_id})` : '連動なし'}
                </p>
                <p className="text-xs text-text-sub mt-1">変更するには休暇種別の閲覧権限が必要です。</p>
              </div>
            ))}
            {draft.category === 'absence' && (
              <div>
                <label className="block text-sm text-text-sub mb-1">区分の意味</label>
                <Select value={draft.absence_kind} onValueChange={(v) => setDraft({ ...draft, absence_kind: v })}>
                  <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ABSENCE_KIND_VALUE}>未設定</SelectItem>
                    {ABSENCE_KINDS.map((k) => <SelectItem key={k} value={k}>{ABSENCE_KIND_LABELS[k]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-text-sub mt-1">
                  変形労働時間制を使う雇用区分の集計に使われます。未設定のままだと、この区分が使われた月の集計が正しく行えない場合があります。
                </p>
              </div>
            )}
            {employmentTypes.length > 0 && (
              <div>
                <label className="block text-sm text-text-sub mb-1">対象の雇用区分 (未選択なら全雇用区分に表示)</label>
                <div className="space-y-1.5 border border-surface-border rounded-lg p-2">
                  {employmentTypes.map((t) => {
                    const checked = draft.employment_type_ids.includes(t.type_id);
                    return (
                      <label key={t.type_id} className="flex items-center gap-2 min-h-[44px] text-sm text-text-body">
                        <input type="checkbox" checked={checked} className="focus-ring" onChange={(e) => setDraft({
                          ...draft,
                          employment_type_ids: e.target.checked
                            ? [...draft.employment_type_ids, t.type_id]
                            : draft.employment_type_ids.filter((id) => id !== t.type_id),
                        })} />
                        {t.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="区分を削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.reason_code_id)} />
    </div>
  );
}
